import { FiltersState, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Crown, Flag, Plus } from '@factorialco/f0-react/icons/app'
import {
  ProcurementTypeFilter,
  ProcurementTypeItemFragment,
  ProcurementTypesDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useProcurementTypeActions } from './hooks/useProcurementTypeActions'

interface Props {
  onCreateProcurementType?: () => void
  onEditProcurementTypeForm: (procurementType: ProcurementTypeItemFragment) => void
  onEditProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onToggleProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onDeleteProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onDuplicateProcurementType: (procurementType: ProcurementTypeItemFragment) => void
}

const ProcurementTypesDataCollection = ({
  onCreateProcurementType,
  onEditProcurementTypeForm,
  onEditProcurementType,
  onToggleProcurementType,
  onDeleteProcurementType,
  onDuplicateProcurementType,
}: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const { getActions } = useProcurementTypeActions({
    onEditProcurementTypeForm: onEditProcurementTypeForm,
    onEditProcurementType: onEditProcurementType,
    onToggle: onToggleProcurementType,
    onDelete: onDeleteProcurementType,
    onDuplicate: onDuplicateProcurementType,
  })

  type DataCollectionInFilter = {
    label: string
    type: 'in'
    options: {
      cache: boolean
      options: () => Promise<Array<{ label: string; value: boolean | number | string }>>
    }
  }

  const resolveFilters = (
    filters: FiltersState<{
      enabled: DataCollectionInFilter
    }>
  ): ProcurementTypeFilter | undefined => {
    const filter: ProcurementTypeFilter = {}

    const enabled = filters?.enabled?.map(Boolean) || []
    if (enabled.length === 1) {
      filter.enabled = { eq: enabled[0] }
    }

    return filter
  }

  const source = useGraphqlDataCollectionSource({
    document: ProcurementTypesDataCollectionDocument,
    primaryActions: () => ({
      label: i18n.t('procurement.purchase_requests.settings.types.actions_new.add'),
      icon: Plus,
      variant: 'default',
      onClick: onCreateProcurementType,
    }),
    mapVariables: ({ sortings, filters }) => ({
      companyId: company.id,
      sortOrder: sortings?.[0],
      filter: resolveFilters(filters),
    }),
    mapData: (data) => data.procurement.typesConnection.nodes,
    mapPagination: (data) => ({
      total: data.procurement.typesConnection.totalCount ?? 0,
    }),
    itemUrl: (item) => {
      const searchParams = new URLSearchParams({ tab: 'form' })
      return (
        navigation.settings.procurement.requestType.id.fullPath({ id: item.id }) +
        '?' +
        searchParams.toString()
      )
    },
    filters: {
      enabled: {
        label: i18n.t(
          'procurement.purchase_requests.settings.data_collection.filters.status.label'
        ),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t(
                'procurement.purchase_requests.settings.data_collection.filters.status.options.enabled'
              ),
              value: true,
            },
            {
              label: i18n.t(
                'procurement.purchase_requests.settings.data_collection.filters.status.options.disabled'
              ),
              value: false,
            },
          ],
        },
      },
    },
    sortings: {
      enabled: {
        label: i18n.t(
          'procurement.purchase_requests.settings.data_collection.sortings.status.enabled'
        ),
      },
    },

    itemActions: (item) => getActions(item),
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'card',
          options: {
            title: ({ name }: { name: string }) => name,
            description: ({ description }: { description: string }) => description,

            cardProperties: [
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.last_updated_at'
                ),
                icon: Calendar,
                render: ({ updatedAt }) => {
                  return {
                    type: 'text',
                    value: i18n.t(
                      'procurement.purchase_requests.settings.types.card_properties.last_updated_at',
                      {
                        date: i18nDateToString(
                          stringToDate(updatedAt, 'backend_datetime'),
                          'ui_date_day_month_year'
                        ),
                      }
                    ),
                  }
                },
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.types.card_properties.author'
                ),
                icon: Crown,
                render: ({ formsConnection }) => {
                  const author = formsConnection?.nodes?.[0]?.author?.employee
                  if (!author) return undefined
                  return {
                    type: 'person',
                    value: {
                      firstName: author?.firstName ?? '',
                      lastName: author?.lastName ?? '',
                      src: buildUrl(author?.avatar?.url ?? ''),
                    },
                  }
                },
              },

              {
                label: '',
                icon: Flag,
                render: (item) => ({
                  type: 'status',
                  value: {
                    status: item.enabled ? 'positive' : 'neutral',
                    label: item.enabled
                      ? i18n.t(
                          'procurement.purchase_requests.settings.types.card_properties.status_enabled'
                        )
                      : i18n.t(
                          'procurement.purchase_requests.settings.types.card_properties.status_disabled'
                        ),
                    variant: item.enabled ? 'positive' : 'neutral',
                  },
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}

export default ProcurementTypesDataCollection
